import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { WorkOrderSeasonData } from 'gql/graphql';
import { CostGroupEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { AddWorkOrderOtherForm } from 'pages/Project/Common/AddWorkOrderOtherForm';
import type { IFormValues } from 'pages/Project/Common/AddWorkOrderOtherForm/helpers';
import { useAddWorkOrderOtherColumns } from 'pages/Project/Common/AddWorkOrderOtherForm/helpers/hooks/useAddWorkOrderOtherColumns';
import { useCheckboxWatcher } from 'pages/Project/Common/AddWorkOrderOtherForm/helpers/hooks/useCheckboxWatcher';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getWorkOrderOther, uerUpdateWorkOrderOtherCostItemsGql } from '../../addOrderOtherQueries';
import { prepareOtherCostData } from '../common/addOtherHelpers';

import { useAddOtherData } from './hooks';

export const AddOthersTable = ({
    onConfirm,
    onCancel,
    constructionTypeId,
    workOrderId,
    workOrderSeasons,
    workOrderStatus,
}: {
    onConfirm: () => void;
    onCancel: () => void;
    constructionTypeId: number;
    workOrderId: number;
    workOrderSeasons: WorkOrderSeasonData[];
    workOrderStatus: WorkOrderStatusEnum | undefined;
}) => {
    const { id: projectId, orderId } = useParams();
    const dispatch = useDispatch();

    const { projectData } = useProjectData(projectId);
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const isVariablesFull = !!(workOrderId && constructionTypeId);

    const [isSubmitted, setIsSubmitted] = useState(false);

    const {
        control,
        setValue,
        watch,
        handleSubmit,
        formState: { isDirty },
    } = useForm<IFormValues>({
        defaultValues: {
            checkedIds: {},
            otherPriceValues: {},
            otherPriceNotes: {},
        },
    });

    const currentCheckedIds = useWatch({
        name: 'checkedIds',
        control,
    });

    const workOrderOthersVariables = useMemo(
        () => ({
            workOrderId,
            constructionTypeId,
        }),
        [constructionTypeId, workOrderId],
    );

    const [makeAddOrderOtherReset] = useResetStoreData({ requestId: 'addWorkOrderOtherCost' });
    const [makeTableDataReset] = useResetStoreData({ requestId: 'addOthersTableData' });

    const onSuccess = useCallback(() => {
        makeAddOrderOtherReset(undefined);
        makeTableDataReset(undefined);
        onConfirm?.();
    }, [makeAddOrderOtherReset, makeTableDataReset, onConfirm]);

    const [workOrderOthers, workOrderOtherStatus] = useRequest({
        requestId: 'addOthersTableData',
        query: getWorkOrderOther,
        withEffect: isVariablesFull,
        variables: workOrderOthersVariables,
    });

    const { groupedData, visibleRowIds, changeVisibleRowIds, visibleRows, nameFilter, initialState } = useAddOtherData({
        workOrderOthers: workOrderOthers?.uerWorkOrderOtherCostSelectionList,
    });

    useEffect(
        () => () => {
            makeAddOrderOtherReset(undefined);
            makeTableDataReset(undefined);
        },
        [makeAddOrderOtherReset, makeTableDataReset],
    );

    useEffect(() => {
        if (groupedData?.length && !Object.keys(currentCheckedIds)?.length) {
            setValue('checkedIds', initialState?.checkedIds, {
                shouldDirty: false,
                shouldTouch: false,
            });
            setValue('otherPriceValues', initialState?.otherPriceValues, {
                shouldDirty: false,
                shouldTouch: false,
            });
            setValue('otherPriceNotes', initialState?.otherPriceNotes, {
                shouldDirty: false,
                shouldTouch: false,
            });
        }
    }, [
        currentCheckedIds,
        groupedData,
        initialState?.checkedIds,
        initialState?.otherPriceNotes,
        initialState?.otherPriceValues,
        setValue,
        workOrderOtherStatus,
    ]);

    const isTableLoading = workOrderOtherStatus === RequestStatus.FETCHING;

    const columns = useAddWorkOrderOtherColumns({
        workOrderSeasons,
        visibleRowIds,
        changeVisibleRowIds,
        control,
        nameFilter,
        workOrderStatus,
    });

    useCheckboxWatcher({
        watch,
        setValue,
    });

    const [
        addWorkOrderOtherCostData,
        addWorkOrderOtherCostStatus,
        addWorkOrderOtherCostErr,
        addWorkOrderOtherCostRequest,
    ] = useRequest({
        requestId: 'addWorkOrderOtherCost',
        query: uerUpdateWorkOrderOtherCostItemsGql,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const handleSave = useCallback(
        (values) => {
            const addWorkOrderOtherCostVariables = {
                workOrderId,
                constructionTypeId,
                otherPriceInputs: prepareOtherCostData({
                    checkedObjects: values?.checkedIds,
                    priceValues: values?.otherPriceValues,
                    priceNotes: values?.otherPriceNotes,
                    workOrderStatus,
                }),
            };

            dispatch(addWorkOrderOtherCostRequest(addWorkOrderOtherCostVariables));
            setIsSubmitted(true);

            if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
                sendGpnAnalyticsEvent({
                    componentId: 'rim_NZ_add to order other button_pressed',
                    projectId,
                    orderId: Number(orderId),
                    addType: CostGroupEnum.Others,
                    orderStatus: WorkOrderStatusEnum.OnFormation,
                    projectName: projectData?.name,
                    projectType: projectData?.contractType,
                    projectDO: projectData?.subsidiary?.name,
                    methodology: projectData?.methodology,
                });
            }

            if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                sendGpnAnalyticsEvent({
                    componentId: 'rim_act KC_add to act KC other button_pressed',
                    projectId,
                    orderId: Number(orderId),
                    orderStatus: WorkOrderStatusEnum.InProgress,
                    projectName: projectData?.name,
                    projectType: projectData?.contractType,
                    projectDO: projectData?.subsidiary?.name,
                    methodology: projectData?.methodology,
                });
            }
        },
        [
            addWorkOrderOtherCostRequest,
            constructionTypeId,
            dispatch,
            orderId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            sendGpnAnalyticsEvent,
            workOrderId,
            workOrderStatus,
        ],
    );

    const handleCreateReport = useCallback(() => {
        ExportCSVReport(addWorkOrderOtherCostData?.uerUpdateWorkOrderOtherCostItems?.validation);
    }, [addWorkOrderOtherCostData?.uerUpdateWorkOrderOtherCostItems?.validation]);

    const handleCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const btnConfirmDisabled = useMemo(
        () => !isDirty || !Object.values(currentCheckedIds).some((item) => item?.parent),
        [currentCheckedIds, isDirty],
    );
    const isAddInProgress = addWorkOrderOtherCostStatus === RequestStatus.FETCHING;

    const btnLabel = useMemo(() => {
        if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
            return 'Добавить в Акт';
        }

        return 'Добавить в Наряд-Заказ';
    }, [workOrderStatus]);

    return (
        <>
            <AddWorkOrderOtherForm
                tableColumns={columns}
                tableData={visibleRows}
                isAddInProgress={isAddInProgress}
                isTableLoading={isTableLoading}
                onConfirm={handleSubmit(handleSave)}
                onCancel={onCancel}
                constructionTypeId={constructionTypeId}
                isDirty={isDirty}
                btnConfirmDisabled={btnConfirmDisabled}
                btnConfirmLabel={btnLabel}
            />

            <ErrorHandlingModal
                titleText="Добавить прочие"
                isSubmitted={isSubmitted}
                statusRequest={addWorkOrderOtherCostStatus}
                dataRequest={addWorkOrderOtherCostData?.uerUpdateWorkOrderOtherCostItems}
                errorsRequest={addWorkOrderOtherCostErr}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={handleCreateReport}
            />
        </>
    );
};

AddOthersTable.displayName = 'AddOthersTable';
